'use client';

import { readConnectionStateSync } from '@/hooks/use-connection-quality';
import { useCallback, useRef } from 'react';

/**
 * Returns a stable callback that warms the HLS manifest at the browser→CDN
 * level when a viewer hovers a video card.
 *
 * Critical constraint: `GET /videos/{id}/play` (videoClient.play /
 * publicClient.getPublicVideoPlay) records a view as a backend side-effect.
 * Any prefetch path that hits that endpoint would create ghost views from
 * the home feed. The legacy implementation of this hook used to call
 * `queryClient.prefetchQuery(['video','play',id])` and was killed for
 * exactly that reason.
 *
 * The only safe warming primitive is a direct CDN fetch of the static HLS
 * manifest URL (no auth header, no view-side-effect path). We skip on
 * `saveData` and `slow`/`offline` connection states so we never penalize
 * data-constrained users; the `'unknown'` quality is treated as fast per the
 * project convention.
 */
export function usePrefetchVideo() {
  const prefetched = useRef(new Set<string>());

  return useCallback((videoId: string, hlsUrl?: string | null): void => {
    if (prefetched.current.has(videoId)) return;
    prefetched.current.add(videoId);

    if (!hlsUrl || typeof fetch !== 'function') return;

    const { quality, saveData } = readConnectionStateSync();
    if (saveData) return;
    if (quality === 'slow' || quality === 'offline') return;

    // `priority` is part of the Fetch Priority API but the TS lib has not
    // landed it yet; cast through `RequestInit` to avoid a literal `any`.
    const init: RequestInit & { priority?: 'high' | 'low' | 'auto' } = {
      credentials: 'omit',
      mode: 'cors',
      priority: 'low',
    };

    fetch(hlsUrl, init).catch(() => {
      // Manifest warm failures are non-fatal — the watch route will fetch
      // again on real navigation. Drop the dedup entry so a re-hover can
      // retry; without this a transient CORS hiccup would permanently
      // disable warming for that uuid in this session.
      prefetched.current.delete(videoId);
    });
  }, []);
}
